import { addStyles } from 'ethcss'
import { color, IStyles } from 'theme'

const focusRing = `0 0 0 3px ${color.blue200}`
const brandGradient = `linear-gradient(135deg, ${color.blue} 0%, ${color.success} 100%)`

const templateEditorInspectorStyles: IStyles = {
    inspector: {
        width: 300,
        flexShrink: 0,
        background: color.bgWhitePrimary,
        borderLeft: `1px solid ${color.borderDarkPrimary}`,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: `inset -3px 0 0 ${color.info}`,
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '14px 16px 11px',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: color.fontDarkMedium,
        borderBottom: `1px solid ${color.blue100}`,
        background: color.blue100,

        '&::before': {
            content: '""',
            width: 3,
            height: 11,
            borderRadius: 2,
            background: color.info,
            flexShrink: 0,
        },
    },
    body: {
        padding: '12px 16px 24px',
    },
}

export const inspectorFieldStyles: IStyles = {
    fieldGroup: {
        marginBottom: 16,
    },
    fieldGroupLabel: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: color.fontDarkLow,
        marginBottom: 8,
    },
    fieldRow: {
        display: 'flex',
        gap: 8,
        marginBottom: 8,
        alignItems: 'flex-end',

        label: {
            fontSize: 12,
            color: color.fontDarkLow,
            minWidth: 24,
        },
    },
    dimField: {
        flex: '1 1 0',
        minWidth: 0,
        width: 'auto',
    },
    fieldRowSpaced: {
        marginTop: 8,
    },
    actionsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 8,
    },
    segmentRow: {
        display: 'flex',
        gap: 0,
        marginBottom: 10,
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 8,
        overflow: 'hidden',
        background: color.bgWhitePrimary,
    },
    segmentButton: {
        flex: 1,
        minWidth: 0,
        padding: '8px 6px',
        border: 'none',
        borderRight: `1px solid ${color.borderDarkPrimary}`,
        background: 'transparent',
        color: color.fontDarkMedium,
        fontFamily: 'inherit',
        fontSize: 11,
        fontWeight: 500,
        cursor: 'pointer',
        appearance: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        lineHeight: 1.2,

        '&:last-child': {
            borderRight: 'none',
        },

        '&:hover': {
            color: color.blue,
            background: color.blue100,
        },

        '&:disabled': {
            opacity: 0.55,
            cursor: 'not-allowed',
        },
    },
    segmentButtonActive: {
        background: color.blue100,
        color: color.blue,
        fontWeight: 600,
    },
    imageToolGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 6,
        marginBottom: 10,
    },
    imageTool: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        minHeight: 52,
        padding: '8px 4px 6px',
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 8,
        background: color.bgWhitePrimary,
        color: color.fontDarkUltraHigh,
        fontFamily: 'inherit',
        fontSize: 10,
        fontWeight: 500,
        lineHeight: 1.2,
        cursor: 'pointer',
        appearance: 'none',
        textAlign: 'center',
        transition: 'border-color 0.15s, background 0.15s, color 0.15s',

        '&:hover': {
            borderColor: color.blue400,
            color: color.blue,
            background: color.blue100,
        },

        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    imageReplace: {
        width: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        minHeight: 34,
        padding: '7px 10px',
        marginBottom: 8,
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 8,
        background: color.bgWhitePrimary,
        color: color.fontDarkUltraHigh,
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
        appearance: 'none',
        transition: 'border-color 0.15s, background 0.15s, color 0.15s',

        '&:hover': {
            borderColor: color.blue400,
            color: color.blue,
            background: color.blue100,
        },

        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    actionButton: {
        flex: '1 1 calc(50% - 4px)',
        minWidth: 0,
        width: '100%',
    },
    actionButtonFull: {
        width: '100%',
    },
    orientationButton: {
        marginTop: 8,
    },
    bgMediaPreview: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 8,
    },
    bgMediaPreviewImage: {
        width: '100%',
        height: 80,
        objectFit: 'cover',
        borderRadius: 8,
        border: `1px solid ${color.borderDarkPrimary}`,
        background: color.bgDarkSecondary,
    },
    bgMediaPreviewName: {
        fontSize: 11,
        color: color.fontDarkMedium,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fieldInput: {
        flex: 1,
        padding: '7px 10px',
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 8,
        fontFamily: 'inherit',
        fontSize: 12,
        width: '100%',
        background: color.bgWhitePrimary,
        color: color.fontDarkUltraHigh,
        transition: 'border-color 0.15s, box-shadow 0.15s',

        '&:hover': {
            borderColor: color.blue300,
        },

        '&:focus': {
            outline: 'none',
            borderColor: color.blue,
            boxShadow: focusRing,
        },
    },
    colorSwatch: {
        width: 28,
        height: 28,
        borderRadius: 6,
        border: `1px solid ${color.borderDarkPrimary}`,
        cursor: 'pointer',
        flexShrink: 0,
        padding: 0,
    },
    fieldCheck: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        fontWeight: 500,
        color: color.fontDarkUltraHigh,
        cursor: 'pointer',
        userSelect: 'none',

        'input[type="checkbox"]': {
            width: 16,
            height: 16,
            accentColor: color.blue,
            flexShrink: 0,
        },
    },
    inspectorBtn: {
        width: '100%',
        padding: '10px 14px',
        marginTop: 8,
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 10,
        background: color.bgWhiteSecondary,
        color: color.fontDarkUltraHigh,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s',

        '&:hover': {
            borderColor: color.blue400,
            color: color.blue,
            background: color.blue100,
        },
    },
    inspectorBtnDanger: {
        background: color.critical100,
        color: color.critical,
        borderColor: `${color.critical}73`,

        '&:hover': {
            background: color.critical,
            color: color.fontWhiteUltraHigh,
            borderColor: color.critical,
        },
    },
}

addStyles(templateEditorInspectorStyles)
addStyles(inspectorFieldStyles)
export default templateEditorInspectorStyles
